import { combineReducers, createReducer } from '@reduxjs/toolkit';
import { Entity } from '../../models/common';
import { set } from '../../utilities/serializable-map';
import {
    createOrReplaceDevBoxAddOn,
    createOrReplaceDevBoxAddOnError,
    createOrReplaceDevBoxAddOnFailed,
    createOrReplaceDevBoxAddOnSuccess,
    enableDevBoxAddOn,
    enableDevBoxAddOnError,
    enableDevBoxAddOnFailed,
    enableDevBoxAddOnSuccess,
    enableDevBoxAddOnWithPolling,
    enableDevBoxAddOnWithPollingError,
    enableDevBoxAddOnWithPollingFailed,
    enableDevBoxAddOnWithPollingSuccess,
    enableDevBoxTunnelPhaseStartDevBox,
    enableDevBoxTunnelPhaseTunnelEnabling,
    enableDevBoxTunnelPhaseTunnelEnablingFinished,
    enableDevBoxTunnelPhaseTunnelNotEnabled,
    getDevBoxAddOnSuccess,
    listDevBoxAddOns,
    listDevBoxAddOnsError,
    listDevBoxAddOnsFailed,
    listDevBoxAddOnsSuccess,
    pollNonTerminalDevBoxAddOnComplete,
} from '../actions/add-ons/add-ons-action-creators';
import { devBoxAddOnsAdapter } from '../adapters/dev-box-add-ons-adapters';
import { AddOnsStatusStore, AddOnsStore, DevBoxAddOnsDataStore, DevBoxTunnelPhase } from '../store/add-ons-store';
import { getPayload } from '../utilities/payload-action';
import { createIndexedStatusReducer } from './indexed-status-reducer';

export const addOnsReducer = combineReducers<AddOnsStore>({
    data: createReducer(DevBoxAddOnsDataStore(), (builder) => {
        builder.addCase(listDevBoxAddOnsSuccess, (store, action) => {
            const { result: addOns, id } = getPayload(action);
            devBoxAddOnsAdapter.setOne(store.devBoxAddOns, Entity(id, addOns));
        });
        builder.addCase(pollNonTerminalDevBoxAddOnComplete, (store, action) => {
            const { result: addOn, id } = getPayload(action);
            if (addOn) {
                devBoxAddOnsAdapter.setOne(store.devBoxAddOns, Entity(id, [addOn]));
            }
        });
        builder.addCase(getDevBoxAddOnSuccess, (store, action) => {
            const { id, result: addOn } = getPayload(action);
            devBoxAddOnsAdapter.setOne(store.devBoxAddOns, Entity(id, [addOn]));
        });
        builder.addCase(createOrReplaceDevBoxAddOnSuccess, (store, action) => {
            const { id, result: addOn } = getPayload(action);
            devBoxAddOnsAdapter.setOne(store.devBoxAddOns, Entity(id, [addOn]));
        });
        builder.addCase(enableDevBoxTunnelPhaseStartDevBox, (store, action) => {
            const { id } = getPayload(action);
            store.devBoxTunnelPhases = set(store.devBoxTunnelPhases, id, DevBoxTunnelPhase.StartDevBox);
        });
        builder.addCase(enableDevBoxTunnelPhaseTunnelEnabling, (store, action) => {
            const { id } = getPayload(action);
            store.devBoxTunnelPhases = set(store.devBoxTunnelPhases, id, DevBoxTunnelPhase.TunnelEnabling);
        });
        builder.addCase(enableDevBoxTunnelPhaseTunnelEnablingFinished, (store, action) => {
            const { id } = getPayload(action);
            store.devBoxTunnelPhases = set(store.devBoxTunnelPhases, id, DevBoxTunnelPhase.TunnelEnablingFinished);
        });
        builder.addCase(enableDevBoxTunnelPhaseTunnelNotEnabled, (store, action) => {
            const { id } = getPayload(action);
            store.devBoxTunnelPhases = set(store.devBoxTunnelPhases, id, DevBoxTunnelPhase.TunnelNotEnabled);
        });
    }),
    status: combineReducers<AddOnsStatusStore>({
        listDevBoxAddOns: createIndexedStatusReducer({
            inProgress: listDevBoxAddOns,
            error: listDevBoxAddOnsError,
            failed: listDevBoxAddOnsFailed,
            success: listDevBoxAddOnsSuccess,
        }),
        enableDevBoxAddOn: createIndexedStatusReducer({
            inProgress: enableDevBoxAddOn,
            error: enableDevBoxAddOnError,
            failed: enableDevBoxAddOnFailed,
            success: enableDevBoxAddOnSuccess,
        }),
        enableDevBoxAddOnWithPolling: createIndexedStatusReducer({
            inProgress: enableDevBoxAddOnWithPolling,
            error: enableDevBoxAddOnWithPollingError,
            failed: enableDevBoxAddOnWithPollingFailed,
            success: enableDevBoxAddOnWithPollingSuccess,
        }),
        createOrReplaceDevBoxAddOn: createIndexedStatusReducer({
            inProgress: createOrReplaceDevBoxAddOn,
            error: createOrReplaceDevBoxAddOnError,
            failed: createOrReplaceDevBoxAddOnFailed,
            success: createOrReplaceDevBoxAddOnSuccess,
        }),
    }),
});
